import type { QueryClient } from '@tanstack/react-query';
import { createRootRouteWithContext, Outlet } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { isStaleChunkError, reloadForNewVersion } from '@web/core/app-version/stale-chunk';
import { NotFound } from '@web/core/layouts/not-found';
import { Button } from '@web/shared/ui/button';

/** The QueryClient travels in the router context so loaders can prefetch. */
export interface RouterContext {
  queryClient: QueryClient;
}

/**
 * The root draws no frame: the `_app` layout draws the application shell, and `/theme` draws
 * its own. Screens outside the shell (a future login, for instance) do not fight a header
 * they should not have.
 */
export const Route = createRootRouteWithContext<RouterContext>()({
  component: Outlet,
  notFoundComponent: () => <NotFound />,
  errorComponent: UnexpectedError,
});

/** Per-route error boundary: a broken screen does not take the whole app down. */
function UnexpectedError({ error }: { error: unknown }) {
  const { t } = useTranslation();
  // A file of the version from before a deploy: reload into the new one (once).
  const stale = isStaleChunkError(error);
  useEffect(() => {
    if (stale) reloadForNewVersion();
  }, [stale]);
  if (stale) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 text-center">
        <h1 className="type-subtitle1">{t('errors.newVersion.title')}</h1>
        <p className="text-muted-foreground max-w-md text-sm">
          {t('errors.newVersion.description')}
        </p>
        <Button onClick={() => globalThis.location.reload()}>{t('common.reload')}</Button>
      </div>
    );
  }
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 text-center">
      <h1 className="type-subtitle1">{t('errors.unexpected.title')}</h1>
      <p className="text-muted-foreground max-w-md text-sm">
        {error instanceof Error ? error.message : t('errors.generic')}
      </p>
      <Button onClick={() => globalThis.location.reload()}>{t('common.reload')}</Button>
    </div>
  );
}
